import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import {
  Container,
  DisplayText,
  HStack,
  LoadingPlaceholder,
  theme,
  useResponsive,
} from '@sg/garnish';

import type {
  LineItemRecommendation,
  RecommendedItemFragment,
} from '@order/graphql';
import { type PartialProduct } from '@order/graphql';
import { useNavigateToPersonalizedProductDetails } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import type { MenuTabStackParamList } from '../../navigation/AppNavigation.props';
import { usePersonalizedRecommendations } from './hooks/';
import { usePersonalizedMenuRecommendationsTelemetry } from './hooks/';
import { MenuRecommendation } from './MenuRecommendation';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuPersonalizationRail = (
  props: MenuPersonalizationRailProps,
) => {
  const { isLoading = false, product } = props;

  const { menuRecommendationsList, isFetching: isFetchingRecommendations } =
    usePersonalizedRecommendations();

  const isFetching = isLoading || isFetchingRecommendations;

  const hasRecommendations =
    Boolean(menuRecommendationsList) && menuRecommendationsList.length > 0;

  usePersonalizedMenuRecommendationsTelemetry({
    isFetching,
    hasRecommendations,
    recommendations: menuRecommendationsList as LineItemRecommendation[],
  });

  // ─────────────────────────────────────────────────────────────────

  if (isFetching) {
    return <MenuPersonalizationRailLoading />;
  }

  if (!isFetching && menuRecommendationsList.length === 0) {
    return null;
  }

  return (
    <MenuPersonalizationRailContainer>
      {menuRecommendationsList.map((recommendation) => (
        <MenuPersonalizationRailItem
          product={product}
          recommendation={recommendation}
          key={recommendation.id}
        />
      ))}
    </MenuPersonalizationRailContainer>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const MenuPersonalizationRailLoading = () => {
  const { match } = useResponsive();

  const loadingPlaceholderHeight = match([164, 278]);

  return (
    <MenuPersonalizationRailContainer>
      <LoadingPlaceholder
        palette="cream"
        rowHeight={loadingPlaceholderHeight}
      />
      <LoadingPlaceholder
        palette="cream"
        rowHeight={loadingPlaceholderHeight}
      />
    </MenuPersonalizationRailContainer>
  );
};

const MenuPersonalizationRailItem = (
  props: MenuPersonalizationRailItemProps,
) => {
  const { product, recommendation } = props;
  const { t } = useLocalizationContext();

  const { track } = useTelemetry();

  const { params } =
    useRoute<RouteProp<MenuTabStackParamList, 'MenuCollection'>>();
  const { restaurantSlug, addressId } = params;

  const navigateToRecommendation = useNavigateToPersonalizedProductDetails({
    restaurantSlug,
    deliveryAddressId: addressId,
    recommendation,
  });

  const { recommendationName, labelName } = useMemo(() => {
    const { name } = recommendation;
    const { recommendationType } = recommendation;

    const historicalRecommendationName =
      recommendationType === 'HISTORICAL'
        ? t('menu-personalization.recommendation.label.classic')
        : t('menu-personalization.recommendation.label.special');

    const genericLabelName = t(
      'menu-personalization.recommendation.label.generic',
    );

    const historicalLabelName =
      recommendationType === 'HISTORICAL'
        ? t('menu-personalization.recommendation.label.classic')
        : t('menu-personalization.recommendation.label.special');

    return {
      recommendationName: name ?? historicalRecommendationName,
      labelName: name ? historicalLabelName : genericLabelName,
    };
  }, [recommendation, t]);

  const onRecommendationCardPressTelemetry = useCallback(() => {
    track('menu.personalization.recommendations.selected', {
      recommendation,
    });
  }, [recommendation, track]);

  const onCardPress = useCallback(() => {
    onRecommendationCardPressTelemetry();
    navigateToRecommendation(product);
  }, [navigateToRecommendation, product, onRecommendationCardPressTelemetry]);

  const cardTheme =
    recommendation.recommendationType === 'HISTORICAL' ? 'spinach' : 'squash';

  const buttonActionTitle = recommendationName.toLowerCase().includes('salad')
    ? t('menu-personalization.recommendation.button.salad')
    : t('menu-personalization.recommendation.button.bowl');

  // ─────────────────────────────────────────────────────────────────

  return (
    <MenuRecommendation
      theme={cardTheme}
      label={labelName}
      title={recommendationName}
      action={buttonActionTitle}
      onPress={onCardPress}
    />
  );
};

const MenuPersonalizationRailContainer = (
  props: MenuPersonalizationRailContainerProps,
) => {
  const { children } = props;

  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const titleFontSize = match([2, 3] as const);
  const cardsPerRow = match([2, 2, 3]);
  const gap = match([theme.spacing['3'], theme.spacing['6']]);

  return (
    <View style={styles.menuPersonalizationWrapper}>
      <Container>
        <DisplayText size={titleFontSize}>
          {t('menu.personalization')}
        </DisplayText>

        <HStack
          itemsPerRow={cardsPerRow}
          gap={gap}
          style={styles.menuPersonalizationStack}
        >
          {children}
        </HStack>
      </Container>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  menuPersonalizationWrapper: {
    paddingTop: theme.spacing['10'],
    backgroundColor: theme.colors.KHAKI_3,
  },

  menuPersonalizationStack: {
    paddingTop: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────
type MenuPersonalizationRailProps = Readonly<{
  product: PartialProduct;
  isLoading?: boolean;
  headerText?: string;
}>;

type MenuPersonalizationRailItemProps = Readonly<{
  product: PartialProduct;
  headerText?: boolean;
  recommendation: RecommendedItemFragment;
}>;

type MenuPersonalizationRailContainerProps = Readonly<{
  children: React.ReactNode;
}>;
